import React, { useMemo, useEffect } from 'react';
import { Modal, Button, Table, Tooltip, Typography, Space, Alert } from 'antd';
import { CheckCircleOutlined, ShopOutlined } from '@ant-design/icons';

const { Text } = Typography;

const ShopifyConfirmShipmentModal = ({ visible, onCancel, onConfirm, shipmentReadyOrders, loading }) => {
    // Add CSS for error rows
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .shopify-confirm-shipment-modal .fallback-row {
                background-color: #fffbe6 !important;
            }
            .shopify-confirm-shipment-modal .fallback-row:hover {
                background-color: #fff1b8 !important;
            }
            .shopify-confirm-shipment-modal .fallback-row td {
                border-bottom: 1px solid #ffe58f;
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    // Check if a carrier needs last mile data (giống Amazon)
    const needsLastMile = (carrier) => {
        return carrier && (carrier.toLowerCase() === 'yunexpress' || carrier.toLowerCase() === 'yanwen');
    };

    // All orders are valid — if last mile exists use it, otherwise fall back to original tracking
    const validOrders = useMemo(() => {
        return shipmentReadyOrders.map(order => ({
            ...order,
            // Determine which tracking will actually be sent:
            // - YunExpress/Yanwen + has last mile → use last mile
            // - YunExpress/Yanwen + no last mile  → fallback to original tracking
            _useLastMile: needsLastMile(order.carrier) && !!order.lastMileTrackingNumber,
            _isFallback:  needsLastMile(order.carrier) && !order.lastMileTrackingNumber,
        }));
    }, [shipmentReadyOrders]);

    const allOrders = validOrders;
    const hasFallbackOrders = validOrders.some(o => o._isFallback);

    const columns = [
        {
            title: '#',
            key: 'index',
            width: 50,
            align: 'center',
            render: (_, record, index) => (
                <Text style={{ fontSize: 12, fontWeight: 'bold' }}>{index + 1}</Text>
            )
        },
        {
            title: 'Order Name',
            dataIndex: 'shopifyOrderName',
            key: 'shopifyOrderName',
            width: 150,
            render: (text) => (
                <Space>
                    <ShopOutlined style={{ color: '#96bf48', fontSize: 12 }} />
                    <Text style={{ fontSize: 12 }}>{text}</Text>
                </Space>
            )
        },
        {
            title: 'Line Item ID',
            dataIndex: 'shopifyLineItemId',
            key: 'shopifyLineItemId',
            width: 140,
            render: (text) => <Text style={{ fontSize: 12, fontFamily: 'monospace' }}>{text}</Text>
        },
        {
            title: 'SKU / Product',
            dataIndex: 'sku',
            key: 'sku',
            ellipsis: true,
            render: (text, record) =>
                <span>
                    <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>{text}</Text><br />
                    <Tooltip title={record.title} placement='topLeft'>
                        <Text style={{ fontSize: 12 }}>{record.title}</Text>
                    </Tooltip>
                </span>
        },
        {
            title: 'Tracking / Carrier',
            dataIndex: 'trackingNumber',
            key: 'trackingCarrier',
            width: 200,
            ellipsis: true,
            render: (text, record) => {
                return (
                    <span>
                        <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>{text || 'N/A'}</Text><br />
                        <Text style={{ fontSize: 12, fontWeight: 'bold' }}>{record.carrier || 'N/A'}</Text>
                    </span>
                );
            }
        },
        {
            title: 'Last Mile / Tracking Used',
            key: 'lastMile',
            width: 230,
            render: (_, record) => {
                if (needsLastMile(record.carrier)) {
                    if (record._useLastMile) {
                        // Has last mile → will use last mile
                        return (
                            <div>
                                <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>{record.lastMileTrackingNumber}</Text><br />
                                <Text style={{ fontSize: 12, fontWeight: 'bold', color: '#52c41a' }}>{record.lastMileCarrier || 'N/A'}</Text>
                                <Text type="secondary" style={{ fontSize: 10, marginLeft: 4 }}>(last mile)</Text>
                            </div>
                        );
                    } else {
                        // No last mile → fallback to original tracking
                        return (
                            <div>
                                <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>{record.trackingNumber}</Text><br />
                                <Text style={{ fontSize: 12, fontWeight: 'bold', color: '#fa8c16' }}>{record.carrier}</Text>
                                <Text type="secondary" style={{ fontSize: 10, marginLeft: 4 }}>(fallback)</Text>
                            </div>
                        );
                    }
                } else {
                    return <Text style={{ fontSize: 11, color: '#999' }}>Not required</Text>;
                }
            }
        },
        {
            title: 'Fulfillment',
            dataIndex: 'fulfillmentStatus',
            key: 'fulfillmentStatus',
            width: 120,
            render: (text) => {
                return (
                    <Text style={{ fontSize: 12 }}>
                        {text || 'N/A'}
                    </Text>
                );
            }
        },
    ];

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    <span>Confirm Shopify Shipment</span>
                </div>
            }
            open={visible}
            onCancel={onCancel}
            footer={[
                <Button key="cancel" onClick={onCancel} disabled={loading}>
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    color='green'
                    variant="solid"
                    icon={<CheckCircleOutlined />}
                    onClick={() => onConfirm(validOrders)}
                    loading={loading}
                    disabled={validOrders.length === 0}
                >
                    Confirm Shipment ({validOrders.length} items)
                </Button>
            ]}
            width='85%'
            className="shopify-confirm-shipment-modal"
            styles={{
                body: {
                    padding: '24px'
                }
            }}
        >
            <div style={{ marginBottom: 16 }}>
                <Text>Are you sure you want to confirm shipment for the following line items?</Text>
                <br />
                <Text type="secondary">This will send tracking information to customers via Shopify.</Text>
            </div>

            {hasFallbackOrders && (
                <Alert
                    message="Some items will use original tracking (fallback)"
                    description={`${validOrders.filter(o => o._isFallback).length} item(s) with YunExpress/Yanwen carrier have no last mile tracking — original tracking/carrier will be used instead.`}
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            <Table
                dataSource={allOrders}
                rowKey="id"
                pagination={false}
                size="small"
                columns={columns}
                scroll={{ y: 400 }}
                rowClassName={(record) => {
                    if (record._isFallback) {
                        return 'fallback-row';
                    }
                    return '';
                }}
            />

            <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f6ffed', borderRadius: 6 }}>
                <Text strong style={{ color: '#52c41a' }}>
                    Total Items: {validOrders.length}
                </Text>
                {hasFallbackOrders && (
                    <Text style={{ marginLeft: 16 }} strong type="warning">
                        Fallback (original tracking): {validOrders.filter(o => o._isFallback).length}
                    </Text>
                )}
            </div>
        </Modal>
    );
};

export default ShopifyConfirmShipmentModal;
